/**
 * AI SEO — Sub-service page
 * Template: same structure as TechnicalSeo.tsx
 */
import React, { useState, useEffect } from "react";
import CaseStudyChart from "@/components/CaseStudyChart";
import SiteLayout from "@/components/SiteLayout";
import IndustriesModule from "@/components/IndustriesModule";
import { useSeoMeta } from "@/hooks/useSeoMeta";
import { useIsMobile } from "@/hooks/useIsMobile";
import ServiceBlogSection from "@/components/ServiceBlogSection";
import LLMVisibilityPromoBlock from "@/components/LLMVisibilityPromoBlock";
import { VIDEO_REVIEW_URL } from "@/lib/cta";

const ff = "'Barlow', sans-serif";
const cyan = "#57d7e2";
const CDN = "https://d2xsxph8kpxj0f.cloudfront.net/310519663444343231/KfbWTnTA9XstaA7FAytXMj";
const LOGO_ASSETS = [
  `/4941-9030.webp`, `/4941-9031.webp`,
  `/4941-9032.webp`, `/4941-9033.webp`,
  `/4941-9034.webp`, `/4941-9035.webp`,
  `/4941-9036.webp`, `/4941-9037.webp`,
  `/4941-9038.webp`, `/4941-9039.webp`,
  `/4941-9040.webp`, `/4941-9041.webp`,
  `/4941-9042.webp`, `/4941-9043.webp`,
  `/4941-9044.webp`, `/4941-9045.webp`,
  `/4941-9046.webp`, `/4941-9047.webp`,
  `/4941-9048.webp`, `/4941-9049.webp`,
  `/4941-9050.webp`, `/4941-9051.webp`,
  `/4941-9052.webp`, `/4941-9053.webp`,
  "https://d2xsxph8kpxj0f.cloudfront.net/310519663444343231/KfbWTnTA9XstaA7FAytXMj/smartfog_87dd78bd.webp",
];
const ctaStyle: React.CSSProperties = {
  display: "inline-flex", alignItems: "center", gap: "8px",
  background: "linear-gradient(135deg, #85e2e9 0%, #20b0bc 100%)",
  border: `1px solid ${cyan}`, borderRadius: "88px", padding: "16px 32px",
  boxShadow: "0px 2px 15px 0px rgba(87,215,226,0.20)",
  color: "#121212", fontSize: "16px", fontWeight: 600, fontFamily: ff,
  textDecoration: "none", transition: "opacity 0.2s",
};
const microCommit: React.CSSProperties = {
  display: "block", color: "rgba(255,255,255,0.4)", fontSize: "13px",
  fontFamily: ff, marginTop: "12px", lineHeight: 1.5,
};

const PAIN_POINTS = [
  { n: "01", title: "AI Overviews are stealing your clicks", desc: "Google's AI Overviews now appear above organic results for 30%+ of queries. If your brand isn't cited in those answers, you're invisible to a growing share of your audience — and losing pipeline to competitors who are." },
  { n: "02", title: "ChatGPT and Perplexity aren't recommending you", desc: "Millions of buyers now use AI assistants to research vendors, compare products, and make purchasing decisions. If your brand doesn't appear in those recommendations, you're not in the consideration set." },
  { n: "03", title: "Zero-click searches are rising — and you're not tracking them", desc: "AI-generated answers mean users get what they need without clicking. If you're not measuring AI search visibility, you have no idea how much demand you're missing — or who's capturing it instead." },
  { n: "04", title: "Your SEO strategy was built for a world that no longer exists", desc: "Traditional SEO optimizes for blue links. GEO (Generative Engine Optimization) optimizes for AI citations. Most agencies are still doing the former while the latter becomes the primary battleground." },
];

const CASE_STUDIES = [
  { slug: "solar-installer", industry: "Renewable Energy", metric: "$1.1m", metricLabel: "Revenue generated", desc: "Over 10 months, Indexed generated $1.5m in SEO-driven pipeline and $1.1m in revenue for a venture-backed solar group — SEO became their highest-return acquisition channel, outperforming Google Ads.", before: "SEO not tracked as revenue channel", after: "$1.1m revenue, $1.5m pipeline", timeframe: "10 months" },
  { slug: "branding-agency", industry: "Branding & Design Services", metric: "3.5×", metricLabel: "Return on investment", desc: "With a $120K SEO investment over 12 months, a global branding studio generated $1.35M in pipeline and $420K in closed revenue — a 250% ROI through the Indexed Full-Stack Search Method™.", before: "$120K SEO investment", after: "$420K revenue, $1.35M pipeline", timeframe: "12 months" },
  { slug: "storage-company", industry: "Storage & Real Estate", metric: "+513%", metricLabel: "Organic click growth", desc: "A national US storage company scaled from 150 to 920 daily organic clicks in 14 months — transforming search into a scalable national acquisition channel.", before: "150 daily organic clicks", after: "920 daily clicks (+513%)", timeframe: "14 months" },
];

const WHO_FOR = [
  { title: "SaaS & Tech", desc: "Products that appear in AI-generated comparison answers and 'best X for Y' queries. If you're not cited, your competitors are." },
  { title: "Professional Services", desc: "Expertise-based businesses where AI assistants recommend specific firms for legal, financial, consulting, and advisory queries." },
  { title: "E-commerce & Brands", desc: "Product categories where AI shopping assistants make recommendations. Being cited in those answers drives direct purchase intent." },
  { title: "Enterprise & Vendor Selection", desc: "Vendor selection queries where buyers ask AI tools to shortlist solutions. If you're not in the AI's training data and citations, you're not on the shortlist." },
];

const RELATED_SEO_SERVICES = [
  { title: "Technical SEO", desc: "Fix the technical foundations that block your content from ranking in both traditional and AI search.", href: "/technical-seo" },
  { title: "SEO Audit & Strategy", desc: "Get a complete picture of your SEO health and a prioritized roadmap to grow.", href: "/seo-audit-and-strategy" },
  { title: "Local SEO", desc: "Dominate local search results and drive qualified foot traffic and leads.", href: "/local-seo" },
  { title: "Content Production", desc: "Publish high-quality, strategically targeted content that earns rankings and AI citations.", href: "/content-production" },
  { title: "Programmatic SEO", desc: "Capture long-tail demand at scale with data-driven, templated page creation.", href: "/programmatic-seo" },
  { title: "Link Building", desc: "Build domain authority through high-quality editorial backlinks that move rankings.", href: "/link-building" },
];

const FAQS = [
  { q: "What is GEO and how is it different from SEO?", a: "GEO (Generative Engine Optimization) is the practice of optimizing your brand and content to appear in AI-generated answers from tools like Google AI Overviews, ChatGPT, and Perplexity. Traditional SEO optimizes for blue-link rankings; GEO optimizes for citations in AI responses. Both matter — which is why the Indexed Full-Stack Search Method™ covers both." },
  { q: "How do you measure AI search visibility?", a: "We track brand mentions and citations across major AI platforms, monitor AI Overview appearances in Google Search Console, and measure the share of voice your brand holds in AI-generated answers for your target queries. We build a baseline in the first 30 days and report on it monthly." },
  { q: "Do you sell standalone GEO or AI SEO audits?", a: "We don't sell one-off audits. AI search visibility is one pillar of the Indexed Full-Stack Search Method™, which covers technical SEO, content, links, GEO, and tracking end-to-end. Book a call and we'll show you how it works for your specific situation." },
  { q: "How quickly will we see results in AI search?", a: "AI search visibility typically improves within 3–6 months of implementing GEO strategies — entity optimization, structured data, and content restructuring for LLM comprehension. The timeline depends on your current authority and how aggressively competitors are pursuing the same queries." },
  { q: "We've invested in SEO — why aren't we showing up in AI answers?", a: "Traditional SEO and GEO require different optimization approaches. Strong rankings don't automatically translate to AI citations. AI models prioritize entities with strong structured data, high-authority mentions, and content structured for comprehension — not just keyword density. We bridge that gap." },
  { q: "How do I know if AI search is costing me revenue?", a: "Most businesses have no idea. That's exactly what our free video review is for. In a 10–15 minute video recorded for you, we'll show you where your brand stands in AI search, who's being cited instead of you, and what that's worth in pipeline. No commitment required." },
];

const NEXT_STEPS = [
  { n: "01", title: "We analyze your AI search visibility gap", desc: "In a free personalized video review, we show you exactly where your brand stands in AI-generated answers — and who's being cited instead of you. You keep a clear picture of what you're losing." },
  { n: "02", title: "We quantify the revenue opportunity", desc: "We estimate the pipeline impact of improving your AI search visibility — not just impressions and clicks, but leads and closed revenue. You'll know the ROI before you commit to anything." },
  { n: "03", title: "We execute the full-stack growth plan", desc: "If it's a fit, we get to work. AI SEO is one pillar of the Indexed Full-Stack Search Method™ — covering technical SEO, content, links, GEO, and tracking, all measured against revenue." },
];

const H2 = ({ children, mb = "64px" }: { children: React.ReactNode; mb?: string }) => (
  <h2 style={{ color: "white", fontSize: "clamp(32px,5vw,48px)", fontWeight: 700, fontFamily: ff, lineHeight: 1.1, letterSpacing: "-0.02em", margin: `0 0 ${mb}`, textAlign: "center" as const, width: "100%" }}>
    {children}
  </h2>
);

export default function AiSeo() {
  useSeoMeta({
    title: "AI SEO Services | Stay Visible in AI-Driven Search",
    description: "Stay visible as search evolves. Our AI SEO services help brands maintain and grow organic presence across AI-powered search engines and generative experiences.",
    canonical: "/ai-seo",
    ogType: "website",
    jsonLd: [
    {
      "@type": "Service",
      "serviceType": "AI SEO",
      "provider": { "@type": "Organization", "name": "Indexed", "url": "https://www.joinindexed.com" },
      "name": "AI SEO & GEO Services",
      "description": "Optimize your brand for generative AI search alongside traditional Google rankings.",
      "url": "https://www.joinindexed.com/ai-seo"
    }
  ,
  
  {
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is GEO and how is it different from SEO?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "GEO (Generative Engine Optimization) is the practice of optimizing your brand and content to appear in AI-generated answers from tools like Google AI Overviews, ChatGPT, and Perplexity. Traditional SEO optimizes for blue-link rankings; GEO optimizes for citations in AI responses. Both matter — which is why the Indexed Full-Stack Search Method™ covers both."
        }
      },
      {
        "@type": "Question",
        "name": "How do you measure AI search visibility?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We track brand mentions and citations across major AI platforms, monitor AI Overview appearances in Google Search Console, and measure the share of voice your brand holds in AI-generated answers for your target queries. We build a baseline in the first 30 days and report on it monthly."
        }
      },
      {
        "@type": "Question",
        "name": "Do you sell standalone GEO or AI SEO audits?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We don't sell one-off audits. AI search visibility is one pillar of the Indexed Full-Stack Search Method™, which covers technical SEO, content, links, GEO, and tracking end-to-end. Book a call and we'll show you how it works for your specific situation."
        }
      },
      {
        "@type": "Question",
        "name": "How quickly will we see results in AI search?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "AI search visibility typically improves within 3–6 months of implementing GEO strategies — entity optimization, structured data, and content restructuring for LLM comprehension. The timeline depends on your current authority and how aggressively competitors are pursuing the same queries."
        }
      },
      {
        "@type": "Question",
        "name": "We've invested in SEO — why aren't we showing up in AI answers?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Traditional SEO and GEO require different optimization approaches. Strong rankings don't automatically translate to AI citations. AI models prioritize entities with strong structured data, high-authority mentions, and content structured for comprehension — not just keyword density. We bridge that gap."
        }
      },
      {
        "@type": "Question",
        "name": "How do I know if AI search is costing me revenue?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Most businesses have no idea. That's exactly what our free video review is for. In a 10–15 minute video recorded for you, we'll show you where your brand stands in AI search, who's being cited instead of you, and what that's worth in pipeline. No commitment required."
        }
      }
    ]
  },
  {
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://www.joinindexed.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Services",
        "item": "https://www.joinindexed.com/seo-agency"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "AI SEO & GEO",
        "item": "https://www.joinindexed.com/ai-seo"
      }
    ]
  }],
  });
  const isMobile = useIsMobile();
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <SiteLayout>

      {/* ── HERO SECTION ── */}
      <div style={{ position: "relative", overflow: "hidden", paddingTop: isMobile ? "60px" : "0" }}>
        {/* Background image */}
        <div style={{
          position: "absolute", top: "-56px", left: "-51px",
          width: "calc(100% + 102px)", height: "calc(100% + 112px)",
          overflow: "hidden", zIndex: 0,
        }}>
          <div style={{ width: "100%", height: "100%", background: "radial-gradient(115% 125% at 78% -10%, #0f3036 0%, #0b1719 38%, #070b0b 72%)" }} />
        </div>
        {/* Cyan glow left */}
        <div style={{ position: "absolute", width: "286px", height: "536px", left: "-80px", top: "-97px", background: "#3aefff", filter: "blur(175px)", opacity: 0.15, transform: "rotate(15deg)", zIndex: 1, pointerEvents: "none" }} />
        {/* Cyan glow center */}
        <div style={{ position: "absolute", width: "474px", height: "474px", left: "473px", top: "-300px", background: "#3aefff", filter: "blur(175px)", opacity: 0.35, transform: "rotate(45deg)", zIndex: 1, pointerEvents: "none" }} />
        {/* Bottom gradient */}
        <div style={{
          position: "absolute", width: "100%", height: "60%", left: 0, bottom: 0,
          background: "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.55) 55%, rgba(0,0,0,0.92) 80%, rgba(0,0,0,1) 100%)",
          zIndex: 2, pointerEvents: "none",
        }} />
        {/* Left text shadow overlay */}
        <div style={{ position: "absolute", top: 0, left: 0, width: "55%", height: "100%", background: "linear-gradient(to right, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.6) 60%, rgba(0,0,0,0) 100%)", zIndex: 3, pointerEvents: "none" }} />
        <div style={{ position: "relative", zIndex: 10, display: "flex", flexDirection: "column", paddingTop: "66px" }}>
          <div style={{ flex: 1, display: "flex", alignItems: "center", padding: isMobile ? "60px 20px 40px" : "60px 72px 40px", maxWidth: "1440px", margin: "0 auto", width: "100%" }}>
            <div style={{ maxWidth: "620px" }}>
              {/* Star rating */}
              <div style={{
                display: "inline-flex", alignItems: "center", gap: "8px",
                marginBottom: "24px", padding: "6px 14px",
                borderRadius: "100px", border: "1px solid rgba(255,255,255,0.12)",
                background: "rgba(255,255,255,0.04)",
              }}>
                <svg width="14" height="14" viewBox="0 0 16 16" fill="#FFD700">
                  <path d="M8 1l1.8 4.6L15 6.2l-3.6 3.5.85 4.95L8 12.3l-4.25 2.35.85-4.95L1 6.2l5.2-.6L8 1z"/>
                </svg>
                <span style={{ color: "rgba(255,255,255,0.85)", fontSize: "14px", fontWeight: 500, fontFamily: ff }}>5.0 from 100+ reviews</span>
              </div>
              {/* Headline */}
              <h1 style={{
                color: "white", fontSize: isMobile ? "36px" : "clamp(40px, 4vw, 60px)", fontWeight: 700,
                lineHeight: 1.08, margin: "0 0 24px", fontFamily: ff, letterSpacing: "-0.5px",
                maxWidth: "560px",
              }}>
                AI SEO & GEO:{" "}<span style={{ color: "#57d7e2" }}>Rank in Google and get cited by AI.</span>
              </h1>
              {/* Description */}
              <p style={{
                color: "rgba(255,255,255,0.75)", fontSize: "18px", fontWeight: 400,
                lineHeight: 1.6, margin: "0 0 36px", fontFamily: ff, maxWidth: "520px",
              }}>
                Your brand is invisible in AI search. We can change that.
              </p>
              {/* CTA */}
              <a
                href={VIDEO_REVIEW_URL}
                style={{
                  display: "inline-flex", alignItems: "center", alignSelf: "flex-start", padding: "16px 32px",
                  background: "linear-gradient(135deg, #85e2e9 0%, #20b0bc 100%)",
                  borderRadius: "88px", border: "1px solid #57d7e2",
                  boxShadow: "0px 2px 15px 0px rgba(87,215,226,0.25)",
                  color: "#121212", fontSize: "18px", fontWeight: 600,
                  fontFamily: ff, textDecoration: "none",
                  transition: "opacity 0.2s, transform 0.2s",
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.opacity = "0.9"; (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(-2px)"; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.opacity = "1"; (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(0)"; }}
              >
                Get my free video review →
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* LOGO STRIP */}
      <section style={{ background: "#080a0a" }}>
        <div style={{ position: "relative", borderTop: "1px solid rgba(255,255,255,0.08)", padding: "16px 0", overflow: "hidden" }}>
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to right, #000202 0%, transparent 12%, transparent 88%, #000202 100%)", zIndex: 2, pointerEvents: "none" }} />
          <div style={{ display: "flex", alignItems: "center", gap: "50px", animation: "scroll-logos 50s linear infinite", width: "max-content" }}>
            {[...LOGO_ASSETS, ...LOGO_ASSETS].map((src, idx) => (
              <img loading="lazy" key={idx} src={src} alt={`client logo ${idx}`} style={{ width: "135px", height: "90px", objectFit: "contain", flexShrink: 0, filter: "grayscale(1) brightness(2)" }} />
            ))}
          </div>
        </div>
      </section>

      {/* PAIN POINTS */}
      <section className="section-grid" style={{ background:"#0c0c0c",padding:isMobile ? "60px 20px" : "100px 72px" }}>
        <div style={{ maxWidth:"1100px",margin:"0 auto" }}>
          <div>
            <div style={{ textAlign:"center",marginBottom:"48px" }}>
              <H2 mb="24px">The AI search gaps<br /><span style={{ color:cyan }}>quietly costing you revenue.</span></H2>
              <p style={{ color:"rgba(255,255,255,0.5)",fontSize:"16px",fontFamily:ff,lineHeight:1.7,margin:"0 auto",maxWidth:"640px" }}>These aren't future problems. They're happening right now — and every month without a GEO strategy is a month your competitors pull further ahead in AI search.</p>
            </div>
            <div style={{ display:"grid",gridTemplateColumns:isMobile ? "1fr" : "repeat(2,1fr)",gap:"16px" }}>
              {PAIN_POINTS.map((p,i)=>(
                <div key={i} style={{ background:"#111111",border:"1px solid rgba(255,255,255,0.07)",borderRadius:"16px",padding:"28px 24px",display:"flex",flexDirection:"column" as const,gap:"12px" }}>
                  <div style={{ color:cyan,fontSize:"11px",fontWeight:700,fontFamily:ff,letterSpacing:"0.1em" }}>{p.n}</div>
                  <h3 style={{ color:"white",fontSize:"17px",fontWeight:700,fontFamily:ff,margin:0,lineHeight:1.3 }}>{p.title}</h3>
                  <p style={{ color:"rgba(255,255,255,0.5)",fontSize:"14px",fontFamily:ff,lineHeight:1.7,margin:0 }}>{p.desc}</p>
                </div>
              ))}
            </div>
            <div style={{ textAlign:"center",marginTop:"40px" }}>
              <a href="/method" style={ctaStyle}>See how it works</a>
            </div>
          </div>
        </div>
      </section>

      {/* LLM VISIBILITY TOOL PROMO */}
      <LLMVisibilityPromoBlock size="full" bg="#080c0e" />

      {/* WHAT IS */}
      <section className="section-grid" style={{ background: "#0a0c0c", padding: isMobile ? "60px 20px" : isMobile ? "60px 20px" : "100px 72px" }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <div style={{ textAlign: "center", maxWidth: "760px", margin: "0 auto" }}>
            <H2 mb="20px">The new frontier of<br /><span style={{ color: cyan }}>search visibility.</span></H2>
            <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "18px", fontFamily: ff, lineHeight: 1.8, margin: 0 }}>
              AI-generated answers now appear above organic results for 30%+ of queries. If your brand isn't cited in those answers, you're invisible to a growing share of your audience — and losing pipeline to competitors who are. GEO (Generative Engine Optimization) is the discipline of making your brand the one that gets recommended.
            </p>
          </div>
        </div>
      </section>

      {/* DELIVERABLES */}
      <section className="section-grid" style={{ background:"#0c0c0c",padding:isMobile ? "60px 20px" : "100px 72px" }}>
        <div style={{ maxWidth:"1100px",margin:"0 auto" }}>
          <H2 mb="20px">What we cover —<br /><span style={{ color:cyan }}>inside every engagement.</span></H2>
          <p style={{ color:"rgba(255,255,255,0.5)",fontSize:"16px",fontFamily:ff,lineHeight:1.75,margin:"0 auto 48px",maxWidth:"640px",textAlign:"center" as const }}>AI SEO is one pillar of the Indexed Full-Stack Search Method™. When you work with us, every layer of your AI search visibility is covered — diagnosed, optimized, and tracked against revenue. Not a one-off audit. A continuous, measurable engagement.</p>
          <div style={{ display:"grid",gridTemplateColumns:isMobile ? "1fr" : "repeat(3,1fr)",gap:"18px" }}>
            {[
              { title:"GEO audit & AI visibility baseline", desc:"We establish where your brand currently stands in AI-generated answers across Google, ChatGPT, and Perplexity — and map the gap to your competitors." },
              { title:"AI citation tracking & monitoring", desc:"Ongoing monitoring of brand mentions and citations in AI responses, so you can see exactly how your visibility changes month over month." },
              { title:"Entity optimization for LLMs", desc:"We structure your brand's entity data so AI models can confidently identify, understand, and recommend you in relevant queries." },
              { title:"Structured data for AI comprehension", desc:"Schema markup and content structure optimized for how large language models parse and cite information — not just how search crawlers index it." },
              { title:"Content restructuring for LLM visibility", desc:"We rewrite and restructure key pages so AI models extract and cite your expertise in the answers your buyers are reading." },
              { title:"Monthly AI search visibility reporting", desc:"Every metric tracked against pipeline — AI citation share, query coverage, brand mention growth — so you always know what's working." },
            ].map((item,i)=>(
              <div key={i} style={{ background:"#111111",border:"1px solid rgba(255,255,255,0.07)",borderRadius:"16px",padding:isMobile ? "28px" : "32px",display:"flex",flexDirection:"column" as const,gap:"14px" }}>
                <div style={{ width:"36px",height:"36px",borderRadius:"50%",background:"rgba(87,215,226,0.08)",border:"1px solid rgba(87,215,226,0.15)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
                  <svg width="16" height="16" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke={cyan} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </div>
                <div style={{ color:"white",fontSize:"16px",fontWeight:700,fontFamily:ff,lineHeight:1.3 }}>{item.title}</div>
                <div style={{ color:"rgba(255,255,255,0.5)",fontSize:"14px",fontFamily:ff,lineHeight:1.65 }}>{item.desc}</div>
              </div>
            ))}
          </div>
          <div style={{ textAlign:"center",marginTop:"48px" }}>
            <a href="/method" style={ctaStyle}>See how it works</a>
          </div>
        </div>
      </section>

      {/* CASE STUDIES */}
      <section className="section-grid" style={{ background: "#080808", padding: isMobile ? "60px 20px" : isMobile ? "60px 20px" : "100px 72px" }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: "16px", marginBottom: "64px" }}>
            <h2 style={{ color: "white", fontSize: "clamp(32px,5vw,48px)", fontWeight: 700, fontFamily: ff, lineHeight: 1.1, letterSpacing: "-0.02em", margin: 0, textAlign: "left" as const }}>Real results,<br /><span style={{ color: cyan }}>real businesses.</span></h2>
            <a href="/client-stories"
              style={{ color: cyan, fontSize: "16px", fontWeight: 600, fontFamily: ff, textDecoration: "none", transition: "opacity 0.2s", flexShrink: 0 }}
              onMouseEnter={e => (e.currentTarget as HTMLAnchorElement).style.opacity = "0.7"}
              onMouseLeave={e => (e.currentTarget as HTMLAnchorElement).style.opacity = "1"}>
              See all →
            </a>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: "18px" }}>
            {CASE_STUDIES.map((cs, i) => (
              <a key={i} href={`/client-stories/${cs.slug}`} style={{ textDecoration: "none", color: "inherit", display: "block" }}>
              <div style={{ background: "#111111", border: "1px solid rgba(255,255,255,0.07)", borderRadius: "16px", padding: "32px", display: "flex", flexDirection: "column", gap: "20px" }}
                onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.borderColor = "rgba(87,215,226,0.25)"; (e.currentTarget as HTMLDivElement).style.transform = "translateY(-2px)"; }}
                onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.borderColor = "rgba(255,255,255,0.07)"; (e.currentTarget as HTMLDivElement).style.transform = "translateY(0)"; }}>
                <span style={{ color: "rgba(255,255,255,0.4)", fontSize: "11px", fontWeight: 700, fontFamily: ff, letterSpacing: "0.1em", textTransform: "uppercase" as const }}>{cs.industry}</span>
                <div>
                  <div style={{ color: cyan, fontSize: "44px", fontWeight: 700, fontFamily: ff, lineHeight: 1 }}>{cs.metric}</div>
                  <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "13px", fontFamily: ff, marginTop: "6px" }}>{cs.metricLabel}</div>
                </div>
                <CaseStudyChart slug={cs.slug} ariaLabel={`${cs.industry}: ${cs.metric} ${cs.metricLabel.toLowerCase()}`} />
                  <p style={{ color: "rgba(255,255,255,0.7)", fontSize: "14px", fontFamily: ff, lineHeight: 1.65, margin: 0, flex: 1 }}>{cs.desc}</p>
                <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: "20px", display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "10px" }}>
                  <div>
                    <div style={{ fontSize: "10px", color: "rgba(255,255,255,0.3)", fontFamily: ff, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase" as const, marginBottom: "4px" }}>Before</div>
                    <div style={{ fontSize: "13px", fontWeight: 600, color: "rgba(255,255,255,0.6)", fontFamily: ff }}>{cs.before}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: "10px", color: cyan, fontFamily: ff, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase" as const, marginBottom: "4px" }}>After</div>
                    <div style={{ fontSize: "13px", fontWeight: 600, color: "white", fontFamily: ff }}>{cs.after}</div>
                  </div>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: "12px", color: "rgba(255,255,255,0.3)", fontFamily: ff }}>Achieved in {cs.timeframe}</span>
                    <span style={{ fontSize: "13px", color: cyan, fontFamily: ff, fontWeight: 600 }}>Read case study →</span>
                  </div>
              </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="section-grid" style={{ background: "#0a0c0c", padding: isMobile ? "60px 20px" : isMobile ? "60px 20px" : "100px 72px" }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <H2 mb="56px">What clients say<br /><span style={{ color: cyan }}>about working with us.</span></H2>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: "18px" }}>
            {[
              { quote: "Exceptional communication throughout the project. The team showed a deep grasp of our goals and delivered a flawless final product that exceeded our expectations.", name: "Flavio", role: "Growth Marketing Director", company: "B2B SaaS" },
              { quote: "Great team to work with, very happy with the consultancy and final documents. They translated complex SEO strategy into something our whole team could act on.", name: "Kirsten", role: "Senior Marketing Manager", company: "Professional Services" },
              { quote: "Indexed turned SEO from a cost center into our highest-ROI acquisition channel. The pipeline numbers speak for themselves — $1.5m in 10 months.", name: "James", role: "CEO", company: "Renewable Energy" },
            ].map((t, i) => (
              <div key={i} style={{ background: "#111111", border: "1px solid rgba(255,255,255,0.07)", borderRadius: "16px", padding: "36px", display: "flex", flexDirection: "column", gap: "24px" }}>
                <div style={{ display: "flex", gap: "3px" }}>
                  {[...Array(5)].map((_, s) => <svg key={s} width="14" height="14" viewBox="0 0 12 12" fill="#57d7e2"><path d="M6 1l1.5 3 3.5.5-2.5 2.5.5 3.5L6 9l-3 1.5.5-3.5L1 4.5l3.5-.5z"/></svg>)}
                </div>
                <p style={{ color: "rgba(255,255,255,0.8)", fontSize: "16px", fontFamily: ff, lineHeight: 1.75, margin: 0, flex: 1, fontStyle: "italic" }}>"{t.quote}"</p>
                <div style={{ borderTop: "1px solid rgba(255,255,255,0.07)", paddingTop: "20px", display: "flex", alignItems: "center", gap: "14px" }}>
                  <div style={{ width: "40px", height: "40px", borderRadius: "50%", background: `linear-gradient(135deg, ${cyan}30, ${cyan}10)`, border: `1px solid ${cyan}40`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <span style={{ color: cyan, fontSize: "15px", fontWeight: 700, fontFamily: ff }}>{t.name[0]}</span>
                  </div>
                  <div>
                    <div style={{ color: "white", fontSize: "14px", fontWeight: 700, fontFamily: ff }}>{t.name}</div>
                    <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "13px", fontFamily: ff }}>{t.role} · {t.company}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* RELATED SERVICES */}
      <section className="section-grid" style={{ background: "#0a0c0c", padding: isMobile ? "60px 20px" : isMobile ? "60px 20px" : "100px 72px" }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: "24px", marginBottom: "56px" }}>
            <h2 style={{ color:"white",fontSize:"clamp(32px,5vw,48px)",fontWeight:700,fontFamily:ff,lineHeight:1.1,letterSpacing:"-0.02em",margin:0,textAlign:"left" as const }}>More from our<br /><span style={{ color: cyan }}>SEO offering.</span></h2>
            <a href="/seo-agency"
              style={{ color:cyan,fontSize:"16px",fontWeight:600,fontFamily:ff,textDecoration:"none",transition:"opacity 0.2s" }}
              onMouseEnter={e => (e.currentTarget as HTMLAnchorElement).style.opacity = "0.7"}
              onMouseLeave={e => (e.currentTarget as HTMLAnchorElement).style.opacity = "1"}>
              View all →
            </a>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: "18px", marginBottom: "18px" }}>
            {RELATED_SEO_SERVICES.slice(0, 3).map((svc, i) => (
              <a key={i} href={svc.href} style={{ background: "#111111", border: "1px solid rgba(255,255,255,0.07)", borderRadius: "16px", padding: "32px", textDecoration: "none", display: "flex", flexDirection: "column", gap: "12px" }}
                onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(87,215,226,0.3)"; (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(-2px)"; }}
                onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(255,255,255,0.07)"; (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(0)"; }}>
                <div style={{ width: "28px", height: "2px", background: cyan, borderRadius: "2px" }} />
                <h3 style={{ color: "white", fontSize: "18px", fontWeight: 700, fontFamily: ff, margin: 0, lineHeight: 1.3 }}>{svc.title}</h3>
                <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "14px", fontFamily: ff, lineHeight: 1.65, margin: 0, flex: 1 }}>{svc.desc}</p>
                <span style={{ color: cyan, fontSize: "14px", fontWeight: 600, fontFamily: ff }}>Learn more →</span>
              </a>
            ))}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: "18px" }}>
            {RELATED_SEO_SERVICES.slice(3).map((svc, i) => (
              <a key={i} href={svc.href} style={{ background: "#111111", border: "1px solid rgba(255,255,255,0.07)", borderRadius: "16px", padding: "32px", textDecoration: "none", display: "flex", flexDirection: "column", gap: "12px" }}
                onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(87,215,226,0.3)"; (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(-2px)"; }}
                onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(255,255,255,0.07)"; (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(0)"; }}>
                <div style={{ width: "28px", height: "2px", background: cyan, borderRadius: "2px" }} />
                <h3 style={{ color: "white", fontSize: "18px", fontWeight: 700, fontFamily: ff, margin: 0, lineHeight: 1.3 }}>{svc.title}</h3>
                <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "14px", fontFamily: ff, lineHeight: 1.65, margin: 0, flex: 1 }}>{svc.desc}</p>
                <span style={{ color: cyan, fontSize: "14px", fontWeight: 600, fontFamily: ff }}>Learn more →</span>
              </a>
            ))}
          </div>
        </div>
      </section>


      {/* ── BLOG ARTICLES ──────────────────────────────────────────── */}
      <ServiceBlogSection categorySlug="ai-seo" isMobile={isMobile} />
      {/* FAQs */}
      <section className="section-grid" style={{ background: "#0a0a0a", padding: isMobile ? "60px 20px" : "100px 72px" }}>
        <div style={{ maxWidth: "860px", margin: "0 auto" }}>
          <H2>Common questions about<br /><span style={{ color: cyan }}>AI SEO & GEO.</span></H2>
          <div>
            {FAQS.map((faq, i) => (
              <FaqItem key={i} faq={faq} index={i} openFaq={openFaq} setOpenFaq={setOpenFaq} isLast={i === FAQS.length - 1} />
            ))}
          </div>
        </div>
      </section>

      {/* WHAT HAPPENS NEXT */}
      <section className="section-grid" style={{ background: "#0c0c0c", padding: isMobile ? "60px 20px" : isMobile ? "60px 20px" : "100px 72px" }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "64px" }}>
            <H2 mb="16px">What happens<br /><span style={{ color: cyan }}>when you reach out.</span></H2>
            <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "18px", fontFamily: ff, margin: "0 auto", maxWidth: "480px", lineHeight: 1.6 }}>No sales calls. No vague proposals. Just a clear process from first contact to measurable results.</p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: "2px", background: "rgba(255,255,255,0.05)", borderRadius: "16px", overflow: "hidden" }}>
            {NEXT_STEPS.map((step, i) => (
              <div key={i} style={{ background: "#111111", padding: "48px 40px" }}>
                <div style={{ color: cyan, fontSize: "13px", fontWeight: 700, fontFamily: ff, letterSpacing: "0.08em", marginBottom: "20px" }}>{step.n}</div>
                <h3 style={{ color: "white", fontSize: "22px", fontWeight: 700, fontFamily: ff, margin: "0 0 16px", lineHeight: 1.2 }}>{step.title}</h3>
                <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "15px", fontFamily: ff, lineHeight: 1.7, margin: 0 }}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

            {/* ── INDUSTRIES ── */}
      <IndustriesModule isMobile={isMobile} />

      {/* ── FREE REVENUE GAP ANALYSIS CTA ── */}
      <section className="section-grid" style={{ background: "#080a0a", padding: isMobile ? "60px 20px" : "100px 72px", position: "relative", overflow: "hidden" }}>
        <div style={{ maxWidth: "760px", margin: "0 auto", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: "32px" }}>
          {/* Heading */}
          <h2 style={{ color: "white", fontSize: isMobile ? "28px" : "48px", fontWeight: 700, fontFamily: ff, margin: 0, lineHeight: 1.15, letterSpacing: "-0.02em" }}>
            Find out what your site is losing.<br />{" "}<span style={{ color: "#57d7e2" }}>It's free.</span>
          </h2>
          {/* Body */}
          <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "18px", fontFamily: ff, lineHeight: 1.7, margin: 0, maxWidth: "520px" }}>
            In a free personalized video review, a top-1% SEO records a walkthrough of your site and shows you exactly what's holding your rankings back — and what fixing it is worth. No pitch. No obligation.
          </p>
          {/* CTA */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "14px" }}>
            <a
              href={VIDEO_REVIEW_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-flex",
                alignItems: "center",
                padding: isMobile ? "14px 28px" : "20px 48px",
                background: "linear-gradient(135deg, #85e2e9 0%, #20b0bc 100%)",
                borderRadius: "88px",
                border: "1px solid #57d7e2",
                boxShadow: "0px 4px 32px rgba(87,215,226,0.30)",
                textDecoration: "none",
                color: "#0a0c0c",
                fontSize: isMobile ? "15px" : "17px",
                fontWeight: 700,
                fontFamily: ff,
                letterSpacing: "-0.01em",
                transition: "opacity 0.2s, transform 0.2s",
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.opacity = "0.88"; (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(-1px)"; }}
              onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.opacity = "1"; (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(0)"; }}
            >
              Get my free video review →
            </a>
            <span style={{ color: "rgba(255,255,255,0.35)", fontSize: "13px", fontFamily: ff }}>
              No call &middot; No pitch &middot; We take on 3–4 new clients per month — request yours to check availability
            </span>
          </div>
          {/* Stat strip */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", marginTop: "16px", borderTop: "1px solid rgba(255,255,255,0.07)", paddingTop: "32px", width: "100%", gap: "0" }}>
            {[
              { metric: "5.0 ★", label: "from 100+ reviews" },
              { metric: "100+", label: "engagements delivered" },
              { metric: "Avg. 3.5×", label: "return on SEO investment" },
            ].map((s, i) => (
              <div key={s.metric} style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "6px", padding: isMobile ? "12px 8px" : "12px 16px", borderLeft: i > 0 ? "1px solid rgba(255,255,255,0.1)" : "none", textAlign: "center" as const }}>
                <span style={{ color: "#57d7e2", fontSize: isMobile ? "18px" : "22px", fontWeight: 700, fontFamily: ff, lineHeight: 1 }}>{s.metric}</span>
                <span style={{ color: "rgba(255,255,255,0.45)", fontSize: isMobile ? "11px" : "13px", fontFamily: ff, lineHeight: 1.3 }}>{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

    </SiteLayout>
  );
}

function FaqItem({ faq, index, openFaq, setOpenFaq, isLast }: { faq: { q: string; a: string }; index: number; openFaq: number | null; setOpenFaq: (i: number | null) => void; isLast: boolean }) {
  const isOpen = openFaq === index;
  const ff = "'Barlow', sans-serif";
  const cyan = "#57d7e2";
  return (
    <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", borderBottom: isLast ? "1px solid rgba(255,255,255,0.08)" : "none" }}>
      <button type="button" onClick={() => setOpenFaq(isOpen ? null : index)} style={{ width: "100%", padding: "24px 0", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px", background: "transparent", border: "none", cursor: "pointer", textAlign: "left" }}>
        <span style={{ fontSize: "17px", fontWeight: 600, color: "white", fontFamily: ff, lineHeight: 1.4 }}>{faq.q}</span>
        <span style={{ color: cyan, fontSize: "24px", fontWeight: 300, flexShrink: 0, transform: isOpen ? "rotate(45deg)" : "none", transition: "transform 0.2s", lineHeight: 1 }}>+</span>
      </button>
      {isOpen && <div style={{ paddingBottom: "24px" }}><p style={{ margin: 0, fontSize: "15px", color: "rgba(255,255,255,0.6)", fontFamily: ff, lineHeight: 1.8 }}>{faq.a}</p></div>}
    </div>
  );
}
